<template>
  <v-card outlined>
    <v-data-table
      :headers="tableHeaders"
      :items="batches"
      :loading="loading"
      :items-per-page="10"
    >
      <template v-slot:item.created_at="{ item }">
        {{ formatScheduledForInput(item.created_at) }}
      </template>
      <template v-slot:item.completed_at="{ item }">
        {{ formatScheduledForInput(item.completed_at) }}
      </template>
      <template v-slot:item.status="{ item }">
        <v-chip :color="getStatusColor(item.status)" small>
          {{ getStatusLabel(item.status) }}
        </v-chip>
      </template>
      <template v-slot:item.stats="{ item }">
        {{ item.success_count }} OK / {{ item.failed_count }} Erreur
      </template>
      <template v-slot:item.planification="{ item }">
        <span
          v-if="item.status === 'completed'"
          style="cursor: pointer"
          @click="showDetails(item)"
        >
          👁️
        </span>
        <span v-else>-</span>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-btn
          v-if="item.status === 'completed'"
          small
          color="primary"
          class="mr-2"
          @click="generateDrafts(item)"
        >
          Envoyer en création
        </v-btn>
        <v-btn
          v-if="item.status === 'completed'"
          small
          outlined
          class="mr-2"
          @click="exportXlsx(item)"
        >
          Exporter Excel
        </v-btn>
        <v-btn icon small @click="deleteBatch(item)"> 🗑️ </v-btn>
      </template>
    </v-data-table>

    <v-dialog v-model="detailsDialog" max-width="1500">
      <v-card>
        <v-card-title>Détails de la planification</v-card-title>
        <v-card-text>
          <v-data-table
            :headers="detailHeaders"
            :items="selectedBatchResults"
            :loading="detailsLoading"
            :items-per-page="10"
            dense
          >
            <template v-slot:item.url="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <a
                    v-bind="attrs"
                    v-on="on"
                    :href="item.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-truncate d-inline-block"
                    style="max-width: 150px"
                  >
                    {{ truncateUrl(item.url) }}
                  </a>
                </template>
                <span>{{ item.url }}</span>
              </v-tooltip>
            </template>
            <template v-slot:item.wordpress_url="{ item }">
              <v-tooltip v-if="item.wordpress_url" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <a
                    v-bind="attrs"
                    v-on="on"
                    :href="item.wordpress_url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-truncate d-inline-block"
                    style="max-width: 150px"
                  >
                    {{ truncateUrl(item.wordpress_url) }}
                  </a>
                </template>
                <span>{{ item.wordpress_url }}</span>
              </v-tooltip>
              <span v-else>-</span>
            </template>
            <template v-slot:item.title="{ item }">
              <v-text-field
                v-model="item.title"
                :disabled="detailsLoading"
                dense
                outlined
                hide-details
                class="my-2"
                style="min-width: 500px"
              ></v-text-field>
            </template>
            <template v-slot:item.scheduled="{ item }">
              <v-text-field
                :value="
                  item.scheduled ? formatScheduledForInput(item.scheduled) : ''
                "
                @input="updateScheduled(item, $event)"
                :disabled="detailsLoading"
                dense
                outlined
                hide-details
                type="datetime-local"
                class="my-2"
                style="width: 250px"
              ></v-text-field>
            </template>
            <template v-slot:item.status="{ item }">
              <v-chip
                :color="item.status === 'success' ? 'success' : 'error'"
                small
              >
                {{ item.status === "success" ? "OK" : "Erreur" }}
              </v-chip>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn
                icon
                small
                color="error"
                :disabled="detailsLoading"
                @click="deleteResult(item)"
              >
                <v-icon small>{{ mdiDelete }}</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-btn
            color="primary"
            :loading="saving"
            :disabled="detailsLoading"
            @click="saveResults"
          >
            Sauvegarder
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="detailsDialog = false"
            >Fermer</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import {
  computed,
  onMounted,
  reactive,
  ref,
  watch,
} from "@vue/composition-api";
import axios from "@axios";
import useAxios from "@/hooks/useAxios";
import { mdiDelete } from "@mdi/js";

export default {
  name: "PlanningHistory",
  props: {
    siteId: {
      type: Number,
      default: null,
    },
    templateId: {
      type: Number,
      default: null,
    },
  },
  setup(props, { emit }) {
    const { axiosGet, axiosDelete, axiosPut } = useAxios();

    const loading = ref(false);
    const detailsLoading = ref(false);
    const saving = ref(false);
    const batches = reactive([]);
    const detailsDialog = ref(false);
    const selectedBatchResults = ref([]);
    const selectedBatchId = ref(null);
    const deletedItems = ref([]);
    const detailsRequestId = ref(0);

    const tableHeaders = [
      { text: "Créé le", value: "created_at" },
      { text: "Terminé le", value: "completed_at" },
      { text: "Total URLs", value: "total_urls" },
      { text: "Résultats", value: "stats" },
      { text: "Statut", value: "status" },
      { text: "Voir", value: "planification", sortable: false },
      { text: "Actions", value: "actions", sortable: false },
    ];

    const detailHeaders = [
      { text: "URL source", value: "url", width: "80px" },
      { text: "Titre", value: "title" },
      { text: "URL WordPress", value: "wordpress_url", width: "120px" },
      { text: "Planifié le", value: "scheduled", width: "180px" },
      { text: "Statut", value: "status", width: "100px" },
      { text: "Actions", value: "actions", sortable: false, width: "80px" },
    ];

    const fetchBatches = async () => {
      if (!props.siteId) return;

      loading.value = true;
      try {
        const response = await axiosGet("/news-radar-services/planning_batch", {
          site_id: props.siteId,
          limit: 100,
        });
        batches.splice(0, batches.length, ...response.data);

        const hasInProgress = batches.some(
          (b) => b.status === "in_progress" || b.status === "pending"
        );
        if (hasInProgress) {
          setTimeout(fetchBatches, 2000);
        }
      } catch (error) {
        console.error("Error fetching batches:", error);
      } finally {
        loading.value = false;
      }
    };

    const truncateUrl = (url) => {
      if (!url) return "";
      if (url.length <= 50) return url;
      return url.substring(0, 50) + "...";
    };

    const getStatusColor = (status) => {
      const colors = {
        in_progress: "info",
        completed: "success",
        failed: "error",
      };
      return colors[status] || "grey";
    };

    const getStatusLabel = (status) => {
      const labels = {
        in_progress: "En cours",
        completed: "Terminé",
        failed: "Échoué",
      };
      return labels[status] || "En cours";
    };

    const generateDrafts = (batch) => {
      emit("generate", batch);
    };

    const showDetails = async (batch) => {
      // Only the latest open dialog may apply its asynchronous detail response.
      const requestId = ++detailsRequestId.value;
      // Cached rows are displayed immediately, but editing stays disabled until
      // the fresh rows (including current WordPress URLs) have been received.
      detailsLoading.value = true;
      selectedBatchResults.value = JSON.parse(
        JSON.stringify(batch.results || [])
      );
      selectedBatchId.value = batch.id;
      deletedItems.value = [];
      detailsDialog.value = true;

      try {
        const response = await axiosGet(
          `/news-radar-services/planning_batch/${batch.id}`
        );
        if (
          requestId !== detailsRequestId.value ||
          selectedBatchId.value !== batch.id ||
          !detailsDialog.value
        ) {
          return;
        }
        selectedBatchResults.value = JSON.parse(
          JSON.stringify(response.data.results || [])
        );
      } catch (error) {
        console.error("Error fetching planning details:", error);
      } finally {
        if (requestId === detailsRequestId.value) {
          detailsLoading.value = false;
        }
      }
    };

    const updateScheduled = (item, value) => {
      if (!value) {
        item.scheduled = null;
      } else {
        // Convertir la valeur du datetime-local en UTC pour uniformiser
        const date = new Date(value);
        const utcDate = new Date(
          date.getTime() + date.getTimezoneOffset() * 60000
        );
        item.scheduled = utcDate
          .toISOString()
          .replace("T", " ")
          .replace("Z", "");
      }
    };

    const formatScheduledForInput = (scheduled) => {
      if (!scheduled) return "";
      const date = new Date(scheduled);
      // Convertir de UTC vers le fuseau horaire local pour l'affichage dans le datetime-local
      const localDate = new Date(
        date.getTime() - date.getTimezoneOffset() * 60000
      );
      const year = localDate.getFullYear();
      const month = String(localDate.getMonth() + 1).padStart(2, "0");
      const day = String(localDate.getDate()).padStart(2, "0");
      const hours = String(localDate.getHours()).padStart(2, "0");
      const minutes = String(localDate.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    const deleteResult = (item) => {
      const index = selectedBatchResults.value.indexOf(item);
      if (index > -1) {
        deletedItems.value.push(item);
        selectedBatchResults.value.splice(index, 1);
      }
    };

    const saveResults = async () => {
      if (!selectedBatchId.value) return;
      saving.value = true;
      try {
        const resultsToSave = selectedBatchResults.value
          .filter((item) => !deletedItems.value.includes(item))
          .map((item) => {
            const result = { ...item };
            // This field is derived from queue_id and refreshed by the API; it
            // must never be copied back into the editable planning JSON.
            delete result.wordpress_url;
            return result;
          });
        await axiosPut(
          `/news-radar-services/planning_batch/${selectedBatchId.value}/results`,
          resultsToSave
        );
        await fetchBatches();
        deletedItems.value = [];
        detailsDialog.value = false;
      } catch (error) {
        console.error("Error saving results:", error);
      } finally {
        saving.value = false;
      }
    };

    const deleteBatch = async (batch) => {
      if (!confirm("Êtes-vous sûr de vouloir supprimer cette planification ?"))
        return;
      try {
        await axiosDelete(`/news-radar-services/planning_batch/${batch.id}`);
        fetchBatches();
      } catch (error) {
        console.error("Error deleting batch:", error);
      }
    };

    const exportXlsx = async (batch) => {
      try {
        const { data } = await axios.get(
          `/news-radar-services/planning_batch/${batch.id}/export_xlsx`,
          { responseType: "blob" }
        );

        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement("a");
        link.href = url;
        link.download = `planning_${batch.id}_${
          new Date().toISOString().split("T")[0]
        }.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Erreur export Excel:", error);
        alert("Erreur lors de l'export Excel");
      }
    };

    watch(() => props.siteId, fetchBatches);
    watch(detailsDialog, (isOpen) => {
      if (!isOpen) {
        // Invalidate any response that arrives after the dialog was closed.
        detailsRequestId.value += 1;
        detailsLoading.value = false;
      }
    });

    onMounted(fetchBatches);

    return {
      loading,
      detailsLoading,
      saving,
      batches,
      tableHeaders,
      detailHeaders,
      detailsDialog,
      selectedBatchResults,
      getStatusColor,
      getStatusLabel,
      generateDrafts,
      showDetails,
      deleteBatch,
      deleteResult,
      exportXlsx,
      fetchBatches,
      saveResults,
      updateScheduled,
      truncateUrl,
      mdiDelete,
      formatScheduledForInput,
    };
  },
};
</script>
